/**
 * 선착순(FCFS) 쿠폰 — 잔여 조회 · 발급 · 모듈 표시 상태 훅.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  type FcfsCouponIssueRequest,
  type FcfsCouponIssueResponse,
  type FcfsCouponIssueStatus,
  type FcfsCouponStatusItem,
  requestGetFcfsCouponStatus,
  requestPostFcfsCouponIssue,
} from '@apis/renewal/fcfsCoupon';
import { PromotionFcfsCouponModule } from '@wconcept/wconcept-common/models/renewal/promotion';
import { useWckQuery } from '@hooks/wckQuery/useWckQuery';
import useGlobalState from '@hooks/useGlobalState';
import useModalManager from '@hooks/useModalManager';

import {
  collectRoundCouponCdList,
  computeFcfsShowCheckingUi,
  formatFcfsActiveCountdown,
  fcfsIsBetweenRoundsEndedGapScenario,
  getFcfsNoticeBar,
  resolveFcfsDisplayRound,
  resolveFcfsPhase,
  resolveFcfsPhaseKeyForDisplayRound,
  resolveFcfsPhaseWithRemaining,
  resolveFcfsRoundRefs,
  type FcfsPhase,
  type FcfsPromotionDisplayBounds,
  type FcfsRoundEntry,
  type FcfsRoundRefs,
} from '@components/pages/promotion/utils/fcfsCouponSchedule';

const FCFS_ISSUE_MESSAGE: Record<FcfsCouponIssueStatus, string> = {
  SUCCESS: '처리되었습니다.',
  PENDING: '쿠폰이 발급되었습니다.\n잠시 후 쿠폰함에서 확인해주세요.',
  ALREADY_ISSUED: '이미 발급 받은 쿠폰입니다.',
  EXHAUSTED: '쿠폰이 모두 소진되었습니다.',
  NOT_AUTHORIZED: '로그인이 필요한 서비스입니다.',
  NOT_STARTED: '아직 발급 시간이 아닙니다.',
  SUSPENDED: '발급이 중단되었습니다.',
  INVALID_COUPON: '현재 발급 가능한 쿠폰이 아닙니다.',
  NOT_AVAILABLE: '발급이 종료되었습니다.',
  FAILED: '쿠폰 발급에 실패했습니다.',
  SYSTEM_ERROR: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
};

function resolveFcfsIssueMessage(
  status: FcfsCouponIssueStatus,
  serverMessage?: string
): string {
  const trimmed = serverMessage?.trim();
  if (status === 'FAILED') {
    return trimmed || FCFS_ISSUE_MESSAGE.FAILED;
  }
  if (status === 'SUCCESS' && trimmed) {
    return trimmed;
  }
  return FCFS_ISSUE_MESSAGE[status] ?? FCFS_ISSUE_MESSAGE.SYSTEM_ERROR;
}

const SUCCESSFUL_ISSUE_STATUS_LIST: FcfsCouponIssueStatus[] = [
  'SUCCESS',
  'PENDING',
  'ALREADY_ISSUED',
];

const STATUS_TRIGGERING_REMAINING_REFRESH: FcfsCouponIssueStatus[] = [
  'SUCCESS',
  'PENDING',
  'EXHAUSTED',
  'SUSPENDED',
  'NOT_AVAILABLE',
  'INVALID_COUPON',
];

function fcfsStatusItemsToRemainingRecord(
  items: FcfsCouponStatusItem[]
): Record<string, number> {
  const record: Record<string, number> = {};
  for (const item of items) {
    const cd = item.couponCd?.trim();
    if (cd && item.remaining != null && !Number.isNaN(Number(item.remaining))) {
      record[cd] = Number(item.remaining);
    }
  }
  return record;
}

export interface UseFcfsCouponStatusParams {
  moduleId: number;
  couponCdList: string[];
  /** ACTIVE 이고 쿠폰 코드가 있을 때만 true */
  enabled: boolean;
}

export interface UseFcfsCouponModuleStateParams {
  module: PromotionFcfsCouponModule;
  moduleId: number;
  /** 설정 시 행사 전·후는 프로모션 `displayStartDate`/`displayEndDate` 기준.(미설정 시 모듈 운영일 폴백) */
  promotionDisplayBounds?: FcfsPromotionDisplayBounds | null;
}

export interface UseFcfsCouponModuleStateResult {
  isClientReady: boolean;
  liveNow: dayjs.Dayjs;
  rawPhase: FcfsPhase;
  roundRefs: FcfsRoundRefs;
  displayRound: FcfsRoundEntry | null;
  /** 직전 회차 종료 직후·다음 회차 1시간 초과 간격 카드/UI */
  betweenRoundsEndedGap: boolean;
  couponCdList: string[];
  remainingByCd: Record<string, number> | undefined;
  isFcfsRemainingPending: boolean;
  isFcfsRemainingError: boolean;
  showCheckingUi: boolean;
  phase: FcfsPhase;
  activeCountdownText: string;
  noticeBar: ReturnType<typeof getFcfsNoticeBar>;
}

interface FcfsCouponIssueOptions {
  onSuccess?: (status: FcfsCouponIssueStatus, message: string) => void;
  onError?: (
    status: FcfsCouponIssueStatus | 'NETWORK_ERROR',
    message: string
  ) => void;
  showAlert?: boolean;
}

interface FcfsCouponIssueState {
  isLoading: boolean;
  error: string | null;
  lastStatus: FcfsCouponIssueStatus | null;
}

interface UseFcfsCouponIssueResult extends FcfsCouponIssueState {
  issue: (
    payload: FcfsCouponIssueRequest,
    options?: FcfsCouponIssueOptions
  ) => Promise<{ success: boolean; status: FcfsCouponIssueStatus | null }>;
  clearError: () => void;
  isLoggedIn: boolean;
}

/** 스케줄·카운트다운용 현재 시각. enabled 시 1초마다 갱신. */
export function useFcfsLiveScheduleNow(enabled: boolean): dayjs.Dayjs {
  const [now, setNow] = useState<dayjs.Dayjs>(() => dayjs());

  useEffect(() => {
    if (!enabled) return undefined;
    setNow(dayjs());
    const timer = window.setInterval(() => {
      setNow(dayjs());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  return now;
}

/** FCFS 쿠폰 코드별 잔여 — enabled 시 1회 조회(폴링·포커스 재요청 없음). */
export function useFcfsCouponStatus({
  moduleId,
  couponCdList,
  enabled,
}: UseFcfsCouponStatusParams) {
  const sortedKeyPart = couponCdList.slice().sort().join('|');

  return useWckQuery({
    queryKey: ['fcfsCouponStatus', moduleId, sortedKeyPart],
    queryFn: async () => {
      const items = await requestGetFcfsCouponStatus(moduleId, couponCdList);
      return fcfsStatusItemsToRemainingRecord(items);
    },
    initialDataEnabled: false,
    enabled,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Number.POSITIVE_INFINITY,
    retry: 2,
  });
}

/** 스케줄 phase · displayRound · 잔여 · showCheckingUi · noticeBar 조합. */
export function useFcfsCouponModuleState({
  module,
  moduleId,
  promotionDisplayBounds,
}: UseFcfsCouponModuleStateParams): UseFcfsCouponModuleStateResult {
  const [isClientReady, setIsClientReady] = useState(false);
  useEffect(() => {
    setIsClientReady(true);
  }, []);

  const liveNow = useFcfsLiveScheduleNow(true);

  const rawPhase = useMemo(
    () => resolveFcfsPhase(module, liveNow, promotionDisplayBounds),
    [module, liveNow, promotionDisplayBounds]
  );

  const roundRefs = useMemo(
    () => resolveFcfsRoundRefs(module.dayScheduleList, liveNow),
    [module.dayScheduleList, liveNow]
  );

  const displayRoundForRemaining = useMemo(
    () =>
      resolveFcfsDisplayRound(
        rawPhase,
        module.dayScheduleList,
        roundRefs,
        liveNow
      ),
    [rawPhase, module.dayScheduleList, roundRefs, liveNow]
  );

  const betweenRoundsEndedGap = useMemo(
    () =>
      fcfsIsBetweenRoundsEndedGapScenario(
        rawPhase,
        roundRefs.lastPast,
        roundRefs.next,
        liveNow
      ),
    [rawPhase, roundRefs.lastPast, roundRefs.next, liveNow]
  );

  const couponCdList = useMemo(() => {
    if (!displayRoundForRemaining) return [];
    return collectRoundCouponCdList(displayRoundForRemaining.round);
  }, [displayRoundForRemaining]);

  const {
    data: remainingByCd,
    isPending: isFcfsRemainingPending,
    isError: isFcfsRemainingError,
  } = useFcfsCouponStatus({
    moduleId,
    couponCdList,
    enabled: rawPhase === 'ACTIVE' && couponCdList.length > 0,
  });

  const showCheckingUi = useMemo(
    () =>
      computeFcfsShowCheckingUi({
        isClientReady,
        rawPhase,
        couponCdListLength: couponCdList.length,
        isFcfsRemainingError,
        remainingByCd,
        isFcfsRemainingPending,
      }),
    [
      isClientReady,
      rawPhase,
      couponCdList.length,
      isFcfsRemainingError,
      remainingByCd,
      isFcfsRemainingPending,
    ]
  );

  const phase = useMemo(
    () =>
      resolveFcfsPhaseWithRemaining(
        rawPhase,
        displayRoundForRemaining,
        remainingByCd,
        module,
        liveNow
      ),
    [rawPhase, displayRoundForRemaining, remainingByCd, module, liveNow]
  );

  const displayRoundResolutionPhase = useMemo(
    () => resolveFcfsPhaseKeyForDisplayRound(rawPhase, phase),
    [rawPhase, phase]
  );

  const displayRound = useMemo(
    () =>
      resolveFcfsDisplayRound(
        displayRoundResolutionPhase,
        module.dayScheduleList,
        roundRefs,
        liveNow
      ),
    [displayRoundResolutionPhase, module.dayScheduleList, roundRefs, liveNow]
  );

  const activeCountdownText = useMemo(() => {
    if (phase !== 'ACTIVE' || !displayRound || !isClientReady) return '';
    return formatFcfsActiveCountdown(displayRound.end, liveNow);
  }, [phase, displayRound, liveNow, isClientReady]);

  const noticeBar = useMemo(
    () =>
      getFcfsNoticeBar(
        phase,
        {
          module,
          active: roundRefs.active,
          next: roundRefs.next,
          issuanceWindow: roundRefs.issuanceWindow,
          lastPast: roundRefs.lastPast,
        },
        liveNow
      ),
    [phase, module, roundRefs, liveNow]
  );

  return {
    isClientReady,
    liveNow,
    rawPhase,
    roundRefs,
    displayRound,
    betweenRoundsEndedGap,
    couponCdList,
    remainingByCd,
    isFcfsRemainingPending,
    isFcfsRemainingError,
    showCheckingUi,
    phase,
    activeCountdownText,
    noticeBar,
  };
}

/** FCFS 쿠폰 단건 발급. PENDING 포함 성공 취급 후 잔여 쿼리 invalidate. */
export const useFcfsCouponIssue = (): UseFcfsCouponIssueResult => {
  const { assistData } = useGlobalState();
  const { openLoginConfirm } = useModalManager();
  const queryClient = useQueryClient();
  const [state, setState] = useState<FcfsCouponIssueState>({
    isLoading: false,
    error: null,
    lastStatus: null,
  });

  const isLoggedIn = !!(
    assistData?.isLogin && assistData?.user?.userNo !== '0'
  );

  const checkLoginAndRedirect = useCallback(async (): Promise<boolean> => {
    if (assistData?.user?.userNo === '0' && !assistData?.isLogin) {
      openLoginConfirm();
      return false;
    }
    return !!(assistData?.isLogin && assistData?.user?.userNo !== '0');
  }, [assistData?.isLogin, assistData?.user?.userNo, openLoginConfirm]);

  const issue = useCallback<UseFcfsCouponIssueResult['issue']>(
    async (payload, options = {}) => {
      const { onSuccess, onError, showAlert = true } = options;
      const loggedIn = await checkLoginAndRedirect();
      if (!loggedIn) {
        return { success: false, status: null };
      }

      setState({ isLoading: true, error: null, lastStatus: null });

      let response: FcfsCouponIssueResponse;
      try {
        response = await requestPostFcfsCouponIssue(payload);
      } catch (error) {
        const message = FCFS_ISSUE_MESSAGE.SYSTEM_ERROR;
        setState({ isLoading: false, error: message, lastStatus: null });
        onError?.('NETWORK_ERROR', message);
        if (showAlert) window.alert(message);
        // eslint-disable-next-line no-console
        console.error('[FCFS_COUPON] issue request failed:', error);
        return { success: false, status: null };
      }

      const rawStatus = response?.status;
      const safeStatus: FcfsCouponIssueStatus = rawStatus ?? 'SYSTEM_ERROR';

      const message = resolveFcfsIssueMessage(safeStatus, response.message);

      const isSuccess = SUCCESSFUL_ISSUE_STATUS_LIST.includes(safeStatus);

      setState({
        isLoading: false,
        error: isSuccess ? null : message,
        lastStatus: safeStatus,
      });

      if (safeStatus === 'NOT_AUTHORIZED') {
        openLoginConfirm();
      }

      if (STATUS_TRIGGERING_REMAINING_REFRESH.includes(safeStatus)) {
        queryClient.invalidateQueries({
          queryKey: ['fcfsCouponStatus', payload.moduleId],
        });
      }

      if (isSuccess) onSuccess?.(safeStatus, message);
      else onError?.(safeStatus, message);

      if (showAlert) window.alert(message);

      return { success: isSuccess, status: safeStatus };
    },
    [checkLoginAndRedirect, openLoginConfirm, queryClient]
  );

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    issue,
    clearError,
    isLoggedIn,
  };
};
